import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Drawer,
  Fab,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react';
import { sendChatMessage } from '../../../api/ChatAgentApi';

import {
  postHogAiInstantSupportClicked,
  postHogAiInstantSupportMessageSent,
  postHogWhatsAppSupportClicked,
  postHogWhatsAppSupportVisibilityShown,
} from '../../../helper/PostHogHelper';
import {
  WHATSAPP_SUPPORT_FLAG,
  isWhatsAppSupportVisible,
} from '../../../constants/featureFlags';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

const drawerWidth = 360;

const AppDrawer: React.FC = () => {
  const posthog = usePostHog();
  const whatsAppVariant = useFeatureFlagVariantKey(WHATSAPP_SUPPORT_FLAG);
  const showWhatsApp = isWhatsAppSupportVisible(whatsAppVariant);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activePanel, setActivePanel] = useState<'menu' | 'aiChat'>('menu');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const quickPrompts = useMemo(
    () => [
      'How do I check my available credits?',
      'How do I book a class?',
      'How do I reschedule a class?',
      'How do I cancel a class?',
      'How to transfer credits between courses?',
    ],
    []
  );

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  };

  const toggleDrawer = () => {
    setDrawerOpen((prev) => !prev);
    if (!drawerOpen) {
      setActivePanel('menu');
    }
  };

  const handleOpenChat = () => {
    setActivePanel('aiChat');
    if (posthog) {
      postHogAiInstantSupportClicked(posthog, { location: 'app_drawer' });
    }
  };

  useEffect(() => {
    if (drawerOpen && posthog && typeof whatsAppVariant === 'string') {
      postHogWhatsAppSupportVisibilityShown(posthog, {
        variant: whatsAppVariant,
        surface: 'app_drawer',
      });
    }
  }, [drawerOpen, posthog, whatsAppVariant]);

  const handleWhatsAppClick = () => {
    const baseUrl = process.env.REACT_APP_WHATS_APP_SUPPORT_BASE_URL;
    if (!baseUrl) return;

    if (posthog) {
      postHogWhatsAppSupportClicked(posthog, {
        location: 'app_drawer',
        variant:
          typeof whatsAppVariant === 'string' ? whatsAppVariant : 'unknown',
      });
    }

    window.open(`${baseUrl}?text=${encodeURIComponent('Hello')}`, '_blank');
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isSending) return;

    const isFirstUserMessage = messages.length === 0;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsSending(true);
    scrollToBottom();

    if (isFirstUserMessage && posthog) {
      postHogAiInstantSupportMessageSent(posthog, {
        location: 'app_drawer',
      });
    }

    try {
      const response = await sendChatMessage({ message: trimmed });
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.reply || 'Sorry, I could not generate a response.',
      };
      setMessages((prev) => [...prev, assistantMessage]);
      scrollToBottom();
    } catch (error) {
      const assistantMessage: ChatMessage = {
        id: `assistant-error-${Date.now()}`,
        role: 'assistant',
        content:
          'Sorry, something went wrong while contacting AI Instant Support. Please try again.',
      };
      setMessages((prev) => [...prev, assistantMessage]);
      scrollToBottom();
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <Fab
        color="primary"
        size="medium"
        onClick={toggleDrawer}
        sx={{
          position: 'fixed',
          right: 20,
          bottom: 20,
          zIndex: 1200,
          boxShadow: 6,
          width: 52,
          height: 52,
        }}
        aria-label="Open help and support"
      >
        <SupportAgentIcon />
      </Fab>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer}
        PaperProps={{
          sx: {
            width: drawerWidth,
            display: 'flex',
            flexDirection: 'column',
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.75,
            bgcolor: 'background.paper',
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {activePanel !== 'menu' ? (
              <IconButton
                size="small"
                onClick={() => setActivePanel('menu')}
                aria-label="Back to app drawer menu"
              >
                <ChatBubbleOutlineIcon fontSize="small" />
              </IconButton>
            ) : (
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  bgcolor: 'primary.main',
                  display: 'grid',
                  placeItems: 'center',
                  color: 'primary.contrastText',
                }}
              >
                <SupportAgentIcon fontSize="small" />
              </Box>
            )}

            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                {activePanel === 'menu'
                  ? 'Quick actions'
                  : 'AI Instant Support'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {activePanel === 'menu'
                  ? 'Jump to help and support'
                  : 'Ask about credits, booking, rescheduling and more'}
              </Typography>
            </Box>

            <IconButton size="small" onClick={toggleDrawer} aria-label="Close">
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        {/* Panels */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {/* Menu panel */}
          <Fade in={activePanel === 'menu'} unmountOnExit>
            <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
              <Stack spacing={1.25}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: 0,
                  }}
                >
                  <List disablePadding>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={handleOpenChat}
                        sx={{ py: 1.25 }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 2,
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              display: 'grid',
                              placeItems: 'center',
                            }}
                          >
                            <AutoAwesomeRoundedIcon fontSize="small" />
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography fontWeight={700}>
                              AI Instant Support
                            </Typography>
                          }
                          secondary="Instant help inside the portal"
                        />
                        <Chip
                          size="small"
                          label="New"
                          color="primary"
                          sx={{ fontWeight: 700 }}
                        />
                      </ListItemButton>
                    </ListItem>

                    {showWhatsApp && (
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={handleWhatsAppClick}
                          sx={{ py: 1.25 }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 2,
                                bgcolor: 'rgba(18, 140, 126, 0.12)',
                                color: '#128c7e',
                                display: 'grid',
                                placeItems: 'center',
                              }}
                            >
                              <WhatsAppIcon fontSize="small" />
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography fontWeight={700}>
                                WhatsApp support
                              </Typography>
                            }
                            secondary="Chat with our team"
                          />
                        </ListItemButton>
                      </ListItem>
                    )}
                  </List>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    Popular questions
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {quickPrompts.map((prompt) => (
                      <Chip
                        key={prompt}
                        label={prompt}
                        variant="outlined"
                        onClick={() => {
                          setActivePanel('aiChat');
                          setInput(prompt);
                        }}
                        sx={{ borderRadius: 2 }}
                      />
                    ))}
                  </Stack>
                </Paper>
              </Stack>
            </Box>
          </Fade>

          {/* AI Chat panel */}
          <Fade in={activePanel === 'aiChat'} unmountOnExit>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: 2,
                }}
              >
                {messages.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={800}>
                      Start with a quick prompt
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5, mb: 1.5 }}
                    >
                      I can help you with credits, bookings, rescheduling and
                      payments.
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {quickPrompts.map((prompt) => (
                        <Chip
                          key={prompt}
                          label={prompt}
                          color="primary"
                          variant="outlined"
                          onClick={() => {
                            setInput(prompt);
                          }}
                          sx={{ borderRadius: 2 }}
                        />
                      ))}
                    </Stack>
                  </Paper>
                ) : (
                  <Stack spacing={1.25}>
                    {messages.map((msg) => {
                      const isUser = msg.role === 'user';
                      return (
                        <Box
                          key={msg.id}
                          sx={{
                            display: 'flex',
                            justifyContent: isUser ? 'flex-end' : 'flex-start',
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '88%',
                              px: 1.5,
                              py: 1.25,
                              borderRadius: 3,
                              borderTopRightRadius: isUser ? 1 : 3,
                              borderTopLeftRadius: isUser ? 3 : 1,
                              bgcolor: isUser
                                ? 'primary.main'
                                : 'background.paper',
                              color: isUser
                                ? 'primary.contrastText'
                                : 'text.primary',
                              boxShadow: isUser ? 0 : 1,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ lineHeight: 1.5 }}
                            >
                              {msg.content}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}

                    {isSending && (
                      <Box
                        sx={{ display: 'flex', justifyContent: 'flex-start' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            borderRadius: 3,
                            px: 1.5,
                            py: 1.25,
                            bgcolor: 'background.paper',
                            width: '70%',
                          }}
                        >
                          <Skeleton width="85%" />
                          <Skeleton width="60%" />
                        </Paper>
                      </Box>
                    )}
                    <div ref={messagesEndRef} />
                  </Stack>
                )}
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                  bgcolor: 'background.paper',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    placeholder="Ask AI Instant Support…"
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        handleSend();
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        bgcolor: 'background.default',
                      },
                    }}
                  />

                  <Button
                    variant="contained"
                    onClick={handleSend}
                    disabled={isSending || !input.trim()}
                    sx={{
                      minWidth: 44,
                      width: 44,
                      height: 44,
                      borderRadius: 3,
                      px: 0,
                      boxShadow: 0,
                    }}
                    aria-label="Send"
                  >
                    <SendIcon fontSize="small" />
                  </Button>
                </Stack>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                >
                  Tip: Press Enter to send, Shift+Enter for a new line.
                </Typography>
              </Box>
            </Box>
          </Fade>
        </Box>
      </Drawer>
    </>
  );
};
export default AppDrawer;
